import { mindUpAuthApi } from 'api/MindUp/mindUpAuthApi';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';
import ArrowLineRight from 'assets/Icons/Arrow/ArrowLineRight';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import BasicButton from 'components/Buttons/BasicButton';
import { connectMindPaths, preciousChildPaths } from 'constants/path';
import { StorageKeys } from 'constants/storageKeys';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import styled from 'styled-components';
import { BackgroundColor, BorderColor, IconColor, OpacityColor, PrimaryColor, TextColor } from 'styles/Global/colorSemantic';
import { ColumnDivider } from 'styles/Global/commonStyles';
import { Body3Medium, Body3Regular, Body3SemiBold, Body5Regular, Body5SemiBold } from 'styles/Global/typography';
import { getRelationText } from 'utils/getTypeText';
import { DiagnosisStatus } from 'utils/statusCode';
import SungnamEnterancePage from '../Enterance/SungNamEnterancePage';
import _ from 'lodash';
import { mindUpHolidayApi } from 'api/MindUp/mindUpHolidayApi';
import { IPurchaseCounselInfo, purchaseCounselingResponse } from 'models/MindUp/purchaseCounselingApiTypes';
import { formatDateFlexible } from 'utils/calculateTime';

interface DiagnosisListItem {
    _id: string;
    patientName: string;
    patientGender: 'male' | 'female';
    patientCode: string;
    relationType: number;
    parentName: string;
    gradeName: string;
    status: number;
    voucherCode: string;
    diagnosisDateTime?: string;
    counselDateTimeList?: string[];
    noShowInfo: any[];
    paymentId?: string;
}

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: start;
    width: 100%;
    padding: 0px 16px 36px;
    gap: 12px;
`;

const Card = styled.div`
    width: 100%;
    border: 1px solid ${BorderColor.color_border_light};
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const DiagnosisInfoContainer = styled.div<{ $isShow?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: ${(props) => (props.$isShow ? '20px 20px 20px 20px' : '20px 20px 0px 20px')};
    gap: 6px;
`;

const ButtonContainer = styled.div`
    display: flex;
    padding: 0px 16px 16px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const NumberBadge = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${BackgroundColor.color_background_primary_normal};
    font-size: 12px;
    font-weight: 600;
`;

const NameAndGender = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
`;

const MetaList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const MetaRow = styled.div`
    display: flex;
    grid-template-columns: 64px 1fr;
    column-gap: 8px;
    row-gap: 2px;
    align-items: center;
    font-size: 13px;
`;

const ResultButtonContainer = styled.button`
    height: 36px;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    background: ${OpacityColor.color_opacity_neutral_8};
    padding: 8px 8px 8px 12px;
    border-radius: 12px;
    cursor: pointer;
`;

const ItemCard = ({ item, idx }: { item: DiagnosisListItem; idx: number; voucher: { code: string; type: number } }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    return (
        <Card>
            <DiagnosisInfoContainer $isShow={Boolean(item.status <= DiagnosisStatus.inProgress || (item.paymentId && _.isEmpty(item.counselDateTimeList || [])))}>
                <Row>
                    <NumberBadge>
                        <Body5SemiBold $color={PrimaryColor.color_primary_normal}>{idx + 1}</Body5SemiBold>
                    </NumberBadge>
                    <NameAndGender>
                        <Body3SemiBold>{item.patientName}</Body3SemiBold>
                        {item.patientGender === 'male' ? <GenderMan /> : <GenderWoman />}
                    </NameAndGender>
                    {(item.status ?? 0) >= DiagnosisStatus.completed && (
                        <ResultButtonContainer
                            onClick={async () => {
                                const voucherModel = await mindUpAuthApi.verifyPurchaseCode({ code: item.voucherCode });
                                let stateValue: any = { diagnosis: item, token: state.token, voucher: voucherModel.data.voucher };

                                if (item.paymentId) {
                                    const response = await mindUpHolidayApi.findPaymentInfo(item.paymentId);
                                    stateValue = { ...stateValue, payment: response.data };
                                }

                                navigate(preciousChildPaths.result, {
                                    replace: false,
                                    state: stateValue,
                                });
                            }}
                        >
                            <Body5SemiBold $color={TextColor.color_text_light}>결과</Body5SemiBold>
                            <ArrowLineRight $width='20' $height='20' $color={IconColor.color_icon_normal} />
                        </ResultButtonContainer>
                    )}
                </Row>
                <MetaList>
                    {(item.status ?? 0) >= DiagnosisStatus.counselReserved && (
                        <MetaRow>
                            <Body5Regular $color={TextColor.color_text_light}>상담일시</Body5Regular>
                            <Body3Regular>{formatDateFlexible(item?.diagnosisDateTime ?? '', 'YYYY/MM/DD a hh:mm')}</Body3Regular>
                        </MetaRow>
                    )}
                    <MetaRow>
                        <Body5Regular $color={TextColor.color_text_light}>학령학기</Body5Regular>
                        <Body3Regular>{item.gradeName}</Body3Regular>
                    </MetaRow>
                    <MetaRow>
                        <Body5Regular $color={TextColor.color_text_light}>주양육자</Body5Regular>
                        <Body3Regular>{getRelationText(item.relationType)}</Body3Regular>
                        <ColumnDivider $backgroundcolor={BorderColor.color_border_normal} $height='12px' />
                        <Body3Regular>{item.parentName}</Body3Regular>
                    </MetaRow>
                </MetaList>
            </DiagnosisInfoContainer>
            <ButtonContainer>
                {item.status <= DiagnosisStatus.inProgress && (
                    <BasicButton
                        $type='fill'
                        $height='44px'
                        $radius='8px'
                        onClick={async () => {
                            const voucherData = await mindUpAuthApi.verifyPurchaseCode({ code: item.voucherCode });
                            console.log('voucherData:: ', voucherData);
                            navigate(connectMindPaths.inspectionGuide, {
                                replace: false,
                                state: {
                                    diagnosis: item,
                                    token: state.token,
                                    voucher: voucherData,
                                },
                            });
                        }}
                    >
                        진행중인 검사 계속하기
                    </BasicButton>
                )}
                {item.paymentId && _.isEmpty(item.counselDateTimeList) && (
                    <BasicButton
                        $type='fill'
                        $height='44px'
                        $radius='8px'
                        onClick={async () => {
                            const voucherModel = await mindUpAuthApi.verifyPurchaseCode({ code: item.voucherCode });
                            navigate(preciousChildPaths.counselReservation, { replace: false, state: { token: state.token, diagnosis: item, voucher: voucherModel } });
                        }}
                    >
                        상담 시간 지정하기
                    </BasicButton>
                )}
            </ButtonContainer>
        </Card>
    );
};

export const PreviousAssessmentsList = () => {
    const { setHeaderText } = useGovernmentHeaderStore();
    const { state } = useLocation();
    const navigate = useNavigate();
    const diagnosisList: DiagnosisListItem[] = state?.diagnosisList;

    useEffect(() => {
        setHeaderText(<Body3Medium>검사 목록</Body3Medium>);
        return () => {
            setHeaderText(null);
        };
    }, [setHeaderText]);

    useBlocker((tx) => {
        const { historyAction } = tx;

        // PUSH/REPLACE는 그대로 허용
        if (historyAction !== 'POP') return false;

        // 뒤로가기 → 엔트런스로 보냄
        const stored = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) || '';
        const rawKey = state?.codeKey || stored;

        // codeKey가 없으면 기본 뒤로가기 허용
        if (!rawKey) return false;

        const target = generatePath(connectMindPaths.sungnamEnterance, {
            codeKey: encodeURIComponent(rawKey),
        });

        // 원래 POP 이동은 "막고", 우리가 원하는 경로로 교체 이동
        navigate(target, { replace: true });
        return true; // ← 원래 이동 차단
    });

    const guard = useRef(false);

    useEffect(() => {
        const onPop = () => {
            if (guard.current) return;
            const stored = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) || '';
            const rawKey = state?.codeKey || stored;
            if (!rawKey) return; // 키 없으면 그대로 뒤로가기

            guard.current = true;
            const target = generatePath(connectMindPaths.sungnamEnterance, {
                codeKey: encodeURIComponent(rawKey),
            });
            navigate(target, { replace: true });
            setTimeout(() => (guard.current = false), 0);
        };

        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [navigate, state]);
    return (
        <Layout>
            <Body5Regular $color={TextColor.color_text_weak}>총 {diagnosisList?.length ?? 0}건</Body5Regular>
            {diagnosisList?.map((e: DiagnosisListItem, idx: number) => {
                return <ItemCard key={e._id ?? idx} item={e} idx={idx} voucher={state.voucher} />;
            })}
        </Layout>
    );
};
